import type { FC, ComponentType } from 'react';
import React, { forwardRef, useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useLazyQuery,
} from '@apollo/react-hooks';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import type { PopupComponentProps } from '@atlaskit/popup/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import Button from '@atlaskit/button/standard-button';

import { ConfluencePageAri } from '@atlassian/ari/confluence/page';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	chooseRandomFromList,
	EMOJI_TITLE_LIST,
} from '@confluence/experiment-default-emoji-page-header';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { isEmojiTitleEnabled } from '@confluence/content-types-utils/entry-points/isEmojiTitleEnabled.ts';
import { ssrEmojiTitleManager } from '@confluence/ssr-utilities/entry-points/SSREmojiTitleManager.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { LoadableAfterPaint } from '@confluence/loadable';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { NTH_USER_EMOJI_TITLE_DISCOVERED } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useUpdateContentProperty } from '@confluence/update-content-property-utils/entry-points/useUpdateContentProperty.ts';

import { EmojiComponent } from '../EmojiComponent.tsx';
import { EmojiPlaceholder } from '../EmojiPlaceholder.tsx';
import { sendAnalyticsEvent } from '../analytics.ts';
import { getEmojiFromEmojiId } from '../emojiMutationHelpers.ts';
import { EmojiPickerLoader } from '../EmojiPickerLoader.ts';
import { getEmojiId, getHasLiveDocsDefaultEmoji, getVersionNumber } from '../transformer.ts';
import { EMOJI_SIZE_MEDIUM, EMOJI_SIZE_LARGE } from '../emojiTitleConstants.ts';
import { useEmojiStatusFieldName } from '../useEmojiStatusFieldName.ts';

import type {
	ContextualTitleEmojiQuery as ContextualTitleEmojiResponse,
	ContextualTitleEmojiQueryVariables,
} from './ContextualTitleEmojiQuery.ts';
// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { UpdateContentPropertyMutation } from './EmojiTitleSectionMutation.experimentalgraphql.ts';
import { EmojiTitleStateContainer } from './EmojiTitleStateContainer.ts';
import { getEmojiTitleProperty } from './EmojiTitleQuery.graphql.ts';
import { useSpaceIcon } from './useSpaceIcon.ts';
import type { getEmojiTitleProperty as getEmojiTitlePropertyType } from './__types__/getEmojiTitleProperty.ts';
import { ContextualTitleEmojiQuery } from './ContextualEmojiTitleQuery.graphql.ts';
import { useEmojiEverSetProperty } from './useEmojiEverSetProperty.ts';
import { useContextualEmojiUserPreferences } from './useContextualEmojiUserPreferences.ts';
import { AutoPageEmojiCohort } from '../__types__/EmojiComponent.ts';

const LiveDocsDefaultIcon = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-LiveDocsDefaultIcon" */ './LiveDocsDefaultIcon.tsx'))
			.LiveDocsDefaultIcon,
	loading: (props) => <EmojiPlaceholder height={props.size} />,
});

export const EMOJI_SIZE_WHITEBOARD = 20;

type EmojiTitleSelectionProps = {
	emojiTitleState: EmojiTitleStateContainer;
	onUpdateStartEmoji?: () => void;
	onUpdateEndEmoji?: () => void;
	isTitleDisabled: boolean;
	contentId: string;
	isTitleCenterAligned?: boolean;
	linkCreation?: boolean;
	hasCoverPicture?: boolean;
	isReadOnly?: boolean;
	contentType?: string;
	updateContentTree?: () => void;
	hasInitialEmoji?: boolean; // used in lazy loading when rendering component placeholder
	pageTitleContent?: string;
	autoPageEmojiCohort?: AutoPageEmojiCohort;
	isUnpublishedDraft?: boolean;
	isEditorEmpty?: boolean;
	emojiTitleDiscovered?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiButtonWrapper = styled.div<{
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	isWhiteboardEmojiFGEnabled?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned && !hasCoverPicture ? 'flex' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned && !hasCoverPicture ? '100%' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned && !hasCoverPicture ? 'center' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignContent: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned && !hasCoverPicture ? 'center' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignItems: ({ isTitleCenterAligned, hasCoverPicture }) =>
		isTitleCenterAligned && !hasCoverPicture ? 'center' : 'initial',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiIconWrapper = styled.div<{
	hasCoverPicture: boolean;
	isWhiteboardEmojiFGEnabled: boolean;
}>({
	display: 'flex',
	justifyContent: 'center',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	padding: 'unset',
	marginTop: 'unset',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EmojiWrapper = styled.div<{
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	isWhiteboardEmojiFGEnabled: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingRight: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? '0px' : token('space.100')),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginLeft: ({ isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled ? `6px` : token('space.0'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const EmojiPlaceholderWrapper = styled.div<{
	shouldHide: boolean;
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	isWhiteboardEmojiFGEnabled: boolean;
}>({
	marginTop: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: ({ isTitleCenterAligned }) =>
		isTitleCenterAligned ? 'initial' : token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingLeft: ({ isTitleCenterAligned, hasCoverPicture }) =>
		/** padding is necessary for left aligning the placeholder with expected location of button once loading is finished
		 * this additional padding comes from the button's base styling
		 */
		isTitleCenterAligned || hasCoverPicture ? 'initial' : token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	display: (props) =>
		props.shouldHide
			? 'none'
			: !props.hasCoverPicture && !props.isTitleCenterAligned
				? 'flex'
				: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> .emoji-placeholder': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		opacity: (props) => (props.shouldHide ? '0' : '1'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiIconOuterWrapper = styled.span<{
	hasCoverPicture: boolean;
	isWhiteboardEmojiFGEnabled: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: ({ hasCoverPicture, isWhiteboardEmojiFGEnabled }) =>
		isWhiteboardEmojiFGEnabled
			? `${EMOJI_SIZE_WHITEBOARD}px`
			: hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceIconImg = styled.img<{
	isTitleCenterAligned?: boolean;
}>({
	borderRadius: token('radius.large'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ isTitleCenterAligned }) =>
		isTitleCenterAligned ? token('space.0') : token('space.050'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginRight: ({ isTitleCenterAligned }) =>
		isTitleCenterAligned ? token('space.0') : token('space.100'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: ({ isTitleCenterAligned }) =>
		isTitleCenterAligned ? token('space.200') : token('space.0'),
	alignSelf: 'center',
	zIndex: 0, //so the space icon appears on top of the cover image
});

// During SSR inline styles have problems getting calculated so needs to be moved into a styled button component
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)<{
	isWhiteboardEmojiFGEnabled: boolean;
	hasCoverPicture?: boolean;
	isReadOnly?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		width: ({ isWhiteboardEmojiFGEnabled, hasCoverPicture }) =>
			isWhiteboardEmojiFGEnabled
				? '24px'
				: hasCoverPicture
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						`${EMOJI_SIZE_LARGE}px`
					: 'initial',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: ({ isWhiteboardEmojiFGEnabled, hasCoverPicture }) =>
			isWhiteboardEmojiFGEnabled
				? '24px'
				: hasCoverPicture
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						`${EMOJI_SIZE_LARGE}px`
					: 'initial',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		cursor: ({ isReadOnly }) => (isReadOnly ? 'auto' : 'pointer'),
	},
});

const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => {
	const { style, ...otherProps } = props;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
	return <div {...otherProps} ref={ref} style={{ ...style }} />;
});

const i18n = defineMessages({
	changeEmoji: {
		id: 'emoji-title.change-emoji',
		description:
			'Text in the button to prompt user to change emoji when they hover over the emoji next to editor title.',
		defaultMessage: 'Change emoji',
	},
	spaceIconAlt: {
		id: 'emoji-title.space-icon-alt',
		description: 'Alt text for the space icon in the editor title.',
		defaultMessage: 'space icon',
	},
	liveDocsDefaultIconLabel: {
		id: 'emoji-title.live-docs-default-icon-label',
		description: 'Label for default live docs distinction icon',
		defaultMessage: 'Live doc emoji',
	},
});

// Returns previous emoji ID
function usePrevious(value: string | undefined): string | undefined {
	const emojiRef = useRef<string | undefined>();

	useEffect(() => {
		emojiRef.current = value;
	}, [value]);

	return emojiRef.current;
}

const EmojiTitleSelectionComponent: FC<EmojiTitleSelectionProps> = ({
	contentId,
	onUpdateStartEmoji,
	onUpdateEndEmoji,
	isTitleDisabled,
	linkCreation,
	emojiTitleState,
	isTitleCenterAligned,
	hasCoverPicture,
	isReadOnly,
	contentType,
	updateContentTree,
	hasInitialEmoji,
	pageTitleContent,
	autoPageEmojiCohort,
	isUnpublishedDraft,
	isEditorEmpty,
	emojiTitleDiscovered,
}) => {
	const intl = useIntl();

	const isLivePage = useIsCurrentPageLive();
	const emojiStatusFieldName = useEmojiStatusFieldName(contentType);
	const { cloudId, userId } = useSessionData();

	const pageAri = useMemo(() => {
		try {
			return ConfluencePageAri.create({
				siteId: cloudId,
				pageId: contentId,
			}).toString();
		} catch {
			return null;
		}
	}, [cloudId, contentId]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		hasEverSetEmoji,
		setEmojiEverSet,
		loading: emojiEverSetPropertyLoading,
		error: emojiEverSetPropertyError,
		updating: emojiEverSetPropertyUpdating,
	} = useEmojiEverSetProperty({
		pageAri,
		contentId,
		createAnalyticsEvent,
		contentType,
		isLivePage,
		autoPageEmojiCohort,
	});
	const { setOnboardingState } = useSetOnboardingState();

	const hasGeneratedEmojiBefore =
		hasEverSetEmoji ||
		emojiEverSetPropertyError ||
		emojiEverSetPropertyLoading ||
		emojiEverSetPropertyUpdating;

	const { isContextualEmojiEnabled } = useContextualEmojiUserPreferences();
	const isWhiteboardEmojiFGEnabled = isEmojiTitleEnabled(contentType);
	const closeBothEmojiPicker = () => {
		void emojiTitleState.setOpenFirstEmojiPicker(false);
		void emojiTitleState.setOpenSecondEmojiPicker(false);
	};

	const [getContextualEmoji, { data: queryData, error: contextualEmojiError }] = useLazyQuery<
		ContextualTitleEmojiResponse,
		ContextualTitleEmojiQueryVariables

		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Local GraphQL operation from same directory
	>(ContextualTitleEmojiQuery);

	// checks that the title is not default text, has characters, not just whitespace
	const isMeaningfulTitle = (title: string): boolean => {
		const trimmedTitle = title.trim();

		return trimmedTitle.length >= 3 && /[a-zA-Z]/.test(trimmedTitle) && !/^\s*$/.test(trimmedTitle);
	};

	// optimistically show placeholder prior to querying for emoji data when
	// parent component has determined that there should be an emoji
	const [showOptimisticPlaceholder, setShowOptimisticPlaceholder] =
		useState<boolean>(!!hasInitialEmoji);

	// Track current emoji data for versioning
	const currentEmojiDataRef = useRef<any>(null);

	const isInRandomEmojiCohort = autoPageEmojiCohort && autoPageEmojiCohort === 'random';

	// Additional checks to see if the user is eligible to get a random emoji on the content they are on.
	// These checks are: the content is a draft page, draft blogpost or live page, the editor is empty, and there is no title.
	const isEligibleForRandomEmojiExperience =
		isInRandomEmojiCohort &&
		(isLivePage || isUnpublishedDraft) &&
		isEditorEmpty &&
		!pageTitleContent;

	const contextualEmojiExp =
		autoPageEmojiCohort && autoPageEmojiCohort === 'ai' && isContextualEmojiEnabled;

	useEffect(() => {
		if (queryData?.confluence_contextualTitleEmoji !== undefined && contextualEmojiExp) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'success',
					actionSubject: 'contextualEmojiQuery',
					source: 'EmojiTitleSelection',
					attributes: {
						cohort: 'ai',
						contentType,
						isLivePage,
					},
				},
			}).fire();
		}
	}, [queryData, contextualEmojiExp, createAnalyticsEvent, contentType, isLivePage]);

	useEffect(() => {
		if (contextualEmojiError && contextualEmojiExp) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'errored',
					actionSubject: 'contextualEmojiQuery',
					source: 'EmojiTitleSelection',
					attributes: {
						cohort: 'ai',
						contentType,
						isLivePage,
					},
				},
			}).fire();
		}
	}, [contextualEmojiError, contextualEmojiExp, createAnalyticsEvent, contentType, isLivePage]);

	const { updateContentProperty } = useUpdateContentProperty({
		contentId,
		contentType: contentType || 'page',
		options: {
			refetchQueries: ['getEmojiTitlePropertyQuery'],
		},
	});

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const [updateContentPropertyExperimentalMutation] = useMutation(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
		UpdateContentPropertyMutation,
		{
			refetchQueries: ['getEmojiTitlePropertyQuery'],
		},
	);

	// Helper function to convert emoji character to Unicode ID
	const emojiToUnicode = (emoji: string): string => {
		const codePoint = emoji.codePointAt(0);
		return codePoint?.toString(16) || '';
	};

	const handleAutoEmojiSelection = useCallback(
		async (autoEmojiId: string) => {
			if (autoEmojiId) {
				// converts emoji character to unicode id
				const isEmojiCharacter = !/^[0-9a-f]+$/i.test(autoEmojiId);
				const unicodeId = isEmojiCharacter ? emojiToUnicode(autoEmojiId) : autoEmojiId;

				// load full emoji data for UI display
				if (!userId) {
					return;
				}

				const loadedEmoji = await getEmojiFromEmojiId({
					userId,
					cloudId,
					emojiId: unicodeId,
				});

				if (loadedEmoji) {
					void emojiTitleState.setSelectedEmoji(loadedEmoji);

					// save to backend
					const propertyKey =
						emojiStatusFieldName === 'emojiTitlePublished'
							? 'emoji-title-published'
							: 'emoji-title-draft';

					const emojiValue = unicodeId.replace(/['"]+/g, '');

					// get current version number from previous emoji data and increment it
					const currentVersion =
						currentEmojiDataRef.current &&
						getVersionNumber(currentEmojiDataRef.current, emojiStatusFieldName);
					const newVersion = currentVersion ? currentVersion + 1 : 1;

					if (expValEquals('migrate_off_experimentalupdatecontentproperty', 'isEnabled', true)) {
						void updateContentProperty({
							propertyKey,
							propertyValue: emojiValue,
							hasExistingProperty: currentVersion > 0,
						});
					} else {
						void updateContentPropertyExperimentalMutation({
							variables: {
								contentId,
								propertyKey,
								propertyValue: emojiValue,
								versionNumber: String(newVersion),
							},
						});
					}
					void setEmojiEverSet(true);
				}
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			emojiTitleState,
			cloudId,
			userId,
			updateContentProperty,
			updateContentPropertyExperimentalMutation,
			contentId,
			emojiStatusFieldName,
		],
	);

	useEffect(() => {
		if (
			!emojiTitleDiscovered &&
			!hasGeneratedEmojiBefore &&
			!emojiTitleState.state.selectedEmoji &&
			isEligibleForRandomEmojiExperience
		) {
			const randomEmojiId = chooseRandomFromList(EMOJI_TITLE_LIST).emoji_value;
			void handleAutoEmojiSelection(randomEmojiId);
		}
	}, [
		emojiTitleDiscovered,
		hasGeneratedEmojiBefore,
		emojiTitleState.state.selectedEmoji,
		isEligibleForRandomEmojiExperience,
		handleAutoEmojiSelection,
	]);

	useEffect(() => {
		if (
			!hasGeneratedEmojiBefore &&
			queryData?.confluence_contextualTitleEmoji != null &&
			!emojiTitleState.state.selectedEmoji &&
			contextualEmojiExp
		) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'generated',
					actionSubject: 'contextualEmoji',
					source: 'EmojiTitleSelection',
					attributes: {
						cohort: 'ai',
						contentType,
						isLivePage,
					},
				},
			}).fire();

			let contextualEmojiId = queryData?.confluence_contextualTitleEmoji;

			// if contextual emoji is empty, default to random emoji
			if (contextualEmojiId === '') {
				contextualEmojiId = chooseRandomFromList(EMOJI_TITLE_LIST).emoji_value;
			}

			void handleAutoEmojiSelection(contextualEmojiId);
		}
	}, [
		queryData?.confluence_contextualTitleEmoji,
		contextualEmojiExp,
		hasGeneratedEmojiBefore,
		emojiTitleState.state.selectedEmoji,
		handleAutoEmojiSelection,
		createAnalyticsEvent,
		contentType,
		isLivePage,
	]);

	useEffect(() => {
		//analytics event for auto emoji exp cohort assignment
		if (
			autoPageEmojiCohort &&
			(autoPageEmojiCohort === 'random' ||
				autoPageEmojiCohort === 'ai' ||
				autoPageEmojiCohort === 'control')
		) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'assigned',
					actionSubject: 'autoEmojiExperiment',
					source: 'EmojiTitleSelection',
					attributes: {
						cohort: autoPageEmojiCohort,
						contentType,
						isLivePage,
					},
				},
			}).fire();
		}
	}, [autoPageEmojiCohort, contentType, isLivePage, createAnalyticsEvent]);

	const isUndefinedLink = isTitleDisabled && linkCreation;
	const shouldQueryEmoji = !isTitleDisabled || isUndefinedLink;
	const {
		data,
		error,
		refetch: refetchEmoji,
		loading,
	} = useQuery<getEmojiTitlePropertyType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		getEmojiTitleProperty,
		{
			variables: {
				contentId,
				status: ['current', 'draft'],
				skipPublishedFragment: false,
				draftShareId: getDraftShareIdFromQueryParam(),
			},
			fetchPolicy: 'cache-and-network',
			skip: !shouldQueryEmoji,
		},
	);
	const emojiId = (data && getEmojiId(data, emojiStatusFieldName)) ?? '';

	// Update ref when data changes
	useEffect(() => {
		currentEmojiDataRef.current = data;
	}, [data]);

	const updateLivePagesDefaultIcon = useCallback(() => {
		if (isLivePage && data) {
			const hasDefaultLiveTitleEmoji = getHasLiveDocsDefaultEmoji(data);
			void emojiTitleState.setHasLiveDocsDefaultEmoji(hasDefaultLiveTitleEmoji);
		}
	}, [data, isLivePage, emojiTitleState]);

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && data) {
		// Populating state container during SSR since we want to rely on it for most up to date info
		void emojiTitleState.setSelectedEmoji(emojiId || null);
		updateLivePagesDefaultIcon();

		// This should set the data which will run on SPA transition
		const emojiTitleData = { queryData: data, emojiStatusFieldName, isLivePage };
		ssrEmojiTitleManager.setEmojiTitleData(emojiTitleData);
	}

	// Use state container since it has the most up to date info
	const hasEmoji = !!emojiTitleState.state.selectedEmoji;

	// Contextual emoji API (only triggered on blur)
	useEffect(() => {
		// Only make contextual emoji API call if there's no existing emoji (user hasn't manually set one)
		// and the user hasn't manually removed an emoji
		// and the user has enabled contextual emoji in their preferences
		if (
			pageTitleContent &&
			isMeaningfulTitle(pageTitleContent) &&
			pageAri &&
			!hasEmoji &&
			!hasGeneratedEmojiBefore &&
			contextualEmojiExp
		) {
			void getContextualEmoji({
				variables: { pageAri, pageTitle: pageTitleContent },
			});
		}
	}, [
		pageTitleContent,
		contentId,
		hasEmoji,
		getContextualEmoji,
		pageAri,
		hasGeneratedEmojiBefore,
		contextualEmojiExp,
	]);

	useEffect(() => {
		if (hasEmoji && emojiId.length > 0) {
			void getEmojiFromEmojiId({
				userId: userId!,
				cloudId,
				emojiId,
			}).then((emoji) => {
				if (emoji) {
					void emojiTitleState.setSelectedEmoji(emoji);
				}
				void emojiTitleState.setLoadingEmoji(false);
			});
		} else {
			if (!loading && emojiTitleState.state.isLoading) {
				void emojiTitleState.setLoadingEmoji(false);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasEmoji, emojiId, loading]);

	// Previous Emoji ID
	const previousEmojiId = usePrevious(emojiId);

	const onEmojiIdChange = previousEmojiId != emojiId;

	// Used to update the content tree emoji when the emoji is changed from the header
	if (onEmojiIdChange && updateContentTree) {
		updateContentTree();
	}

	const { spaceIconPath } = useSpaceIcon({
		emojiLoading: loading,
		hasSelectedEmoji: Boolean(emojiTitleState.state.selectedEmoji),
		contentId,
	});

	// Live docs default icon and optimistic placeholder
	useEffect(() => {
		const setEmojiStateAndUpdateOptimisticPlaceholder = async () => {
			// When default emoji is enabled, we need to wait until after the emojiTitleState is set
			// to stop rendering the optimistic placeholder in order to prevent flashing
			updateLivePagesDefaultIcon();
			if (data) {
				setShowOptimisticPlaceholder(false);
			}
		};

		void setEmojiStateAndUpdateOptimisticPlaceholder();
	}, [data, updateLivePagesDefaultIcon]);

	// clean up emojiTitleState on unmount
	useEffect(() => {
		return () => {
			void emojiTitleState.setSelectedEmoji(null);
			void emojiTitleState.setHasLiveDocsDefaultEmoji(false);
		};
	}, [emojiTitleState]);

	const getEmojiSize = () =>
		isWhiteboardEmojiFGEnabled
			? EMOJI_SIZE_WHITEBOARD
			: hasCoverPicture
				? EMOJI_SIZE_LARGE
				: EMOJI_SIZE_MEDIUM;

	// Use state container since it has the most up to date info
	const shouldRenderEmoji = Boolean(
		hasEmoji && !emojiTitleState.state.isUpdatingEmoji && emojiTitleState.state.selectedEmoji,
	);

	const hasDefaultLiveEmoji =
		emojiTitleState.state.hasLiveDocsDefaultEmoji &&
		!emojiTitleState.state.isRemovingEmoji &&
		!emojiTitleState.state.isUpdatingEmoji;

	const shouldRenderDefaultLiveEmoji = hasDefaultLiveEmoji && !hasEmoji;

	const shouldHidePlaceholder = !hasEmoji && !hasDefaultLiveEmoji && !showOptimisticPlaceholder;

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR && expValEquals('platform_editor_hydratable_ui', 'isEnabled', true)) {
		return (
			<EmojiPlaceholderWrapper
				data-testid="emoji-placeholder"
				shouldHide={shouldHidePlaceholder}
				aria-hidden={shouldHidePlaceholder}
				hasCoverPicture={hasCoverPicture}
				isWhiteboardEmojiFGEnabled={isWhiteboardEmojiFGEnabled}
				isTitleCenterAligned={isTitleCenterAligned}
			>
				{error ? <ErrorDisplay error={error} /> : null}
				<EmojiPlaceholder
					height={getEmojiSize()}
					// @ts-expect-error TODO FIXME `className` is not a valid property
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="emoji-placeholder"
				/>
			</EmojiPlaceholderWrapper>
		);
	}

	if (shouldRenderEmoji || shouldRenderDefaultLiveEmoji) {
		return (
			<EmojiWrapper
				data-testid={isTitleCenterAligned ? 'emoji-wrapper-centered' : 'emoji-wrapper'}
				isTitleCenterAligned={isTitleCenterAligned}
				isWhiteboardEmojiFGEnabled={isWhiteboardEmojiFGEnabled}
			>
				{error ? <ErrorDisplay error={error} /> : null}
				<Popup
					autoFocus={false}
					isOpen={emojiTitleState.state.openFirstEmojiPicker}
					onClose={() => {}}
					popupComponent={PopupComponent as ComponentType<PopupComponentProps>}
					content={() => (
						<EmojiPickerLoader
							contentId={contentId}
							onUpdateStartEmoji={onUpdateStartEmoji}
							emojiTitleState={emojiTitleState}
							onEmojiUpdated={refetchEmoji}
							onUpdateEndEmoji={onUpdateEndEmoji}
							updateContentPropertyExperimentalMutation={updateContentPropertyExperimentalMutation}
							updateContentProperty={updateContentProperty}
							closeBothEmojiPicker={closeBothEmojiPicker}
							componentName="EmojiTitleSelection"
							contentType={contentType}
							autoPageEmojiCohort={autoPageEmojiCohort}
						/>
					)}
					placement="bottom-start"
					trigger={(triggerProps) => (
						<EmojiButtonWrapper
							isTitleCenterAligned={isTitleCenterAligned}
							hasCoverPicture={hasCoverPicture}
							isWhiteboardEmojiFGEnabled={isWhiteboardEmojiFGEnabled}
						>
							<StyledButton
								{...triggerProps}
								appearance="subtle"
								testId="emoji-button"
								shouldFitContainer
								hasCoverPicture={hasCoverPicture}
								isWhiteboardEmojiFGEnabled={isWhiteboardEmojiFGEnabled}
								isReadOnly={isReadOnly}
								isDisabled={isReadOnly || isUndefinedLink}
								onClick={() => {
									void emojiTitleState.setOpenFirstEmojiPicker(
										!emojiTitleState.state.openFirstEmojiPicker,
									);

									void emojiTitleState.setOpenSecondEmojiPicker(false);
									if (isInRandomEmojiCohort && !emojiTitleDiscovered) {
										setOnboardingState({
											key: NTH_USER_EMOJI_TITLE_DISCOVERED.EMOJI_TITLE_DISCOVERED,
											value: 'true',
										});

										createAnalyticsEvent({
											type: 'sendTrackEvent',
											data: {
												action: 'completed',
												actionSubject: 'emojiTitleDiscoveredOnboarding',
												source: 'EmojiTitleSelection',
												attributes: {
													cohort: 'random',
													contentType,
													isLivePage,
												},
											},
										}).fire();
									}

									sendAnalyticsEvent(createAnalyticsEvent, {
										eventType: 'sendUIEvent',
										element: 'button',
										action: 'clicked',
										actionSubject: 'editEmojiButton',
										actionSubjectId: 'emojiIcon',
										attributes: {
											cohort: autoPageEmojiCohort,
											contentType,
											isLivePage,
										},
									});
								}}
								iconBefore={
									<EmojiIconOuterWrapper
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className="emoji-icon"
										data-testid="emoji-icon"
										hasCoverPicture={!!hasCoverPicture}
										isWhiteboardEmojiFGEnabled={isWhiteboardEmojiFGEnabled}
									>
										<Tooltip
											content={intl.formatMessage(i18n.changeEmoji)}
											position={hasCoverPicture ? 'top' : 'left'}
										>
											{shouldRenderDefaultLiveEmoji ? (
												<LiveDocsDefaultIcon
													label={intl.formatMessage(i18n.liveDocsDefaultIconLabel)}
													size={getEmojiSize()}
													testId="live-docs-default-title-icon"
												/>
											) : (
												<EmojiIconWrapper
													hasCoverPicture={!!hasCoverPicture}
													isWhiteboardEmojiFGEnabled={isWhiteboardEmojiFGEnabled}
												>
													<EmojiComponent
														emoji={emojiTitleState.state.selectedEmoji}
														height={getEmojiSize()}
														context="editor"
														renderResourcedEmoji
														hasCoverPicture={hasCoverPicture}
														isPageTitleComponent
													/>
												</EmojiIconWrapper>
											)}
										</Tooltip>
									</EmojiIconOuterWrapper>
								}
							/>
						</EmojiButtonWrapper>
					)}
				/>
			</EmojiWrapper>
		);
	} else if (!isWhiteboardEmojiFGEnabled) {
		if (spaceIconPath) {
			return (
				<SpaceIconImg
					src={`${CONTEXT_PATH}${spaceIconPath}`}
					height={getEmojiSize()}
					width={getEmojiSize()}
					isTitleCenterAligned={isTitleCenterAligned}
					alt={intl.formatMessage(i18n.spaceIconAlt)}
				/>
			);
		} else {
			return (
				<EmojiPlaceholderWrapper
					data-testid="emoji-placeholder"
					shouldHide={shouldHidePlaceholder}
					aria-hidden={shouldHidePlaceholder}
					hasCoverPicture={hasCoverPicture}
					isWhiteboardEmojiFGEnabled={isWhiteboardEmojiFGEnabled}
					isTitleCenterAligned={isTitleCenterAligned}
				>
					{error ? <ErrorDisplay error={error} /> : null}
					<EmojiPlaceholder
						height={getEmojiSize()}
						// @ts-expect-error TODO FIXME `className` is not a valid property
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className="emoji-placeholder"
					/>
				</EmojiPlaceholderWrapper>
			);
		}
	}
};

export const EmojiTitleSelection = (props: Omit<EmojiTitleSelectionProps, 'emojiTitleState'>) => (
	<Subscribe to={[EmojiTitleStateContainer]}>
		{(emojiTitleState: EmojiTitleStateContainer) => (
			<EmojiTitleSelectionComponent
				data-testid="emoji-title-selection"
				{...props}
				emojiTitleState={emojiTitleState}
			/>
		)}
	</Subscribe>
);
EmojiTitleSelection.displayName = 'EmojiTitleSelection';
